import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useRef, useState, type ReactNode } from "react";
import { Link, Navigate, useNavigate, useParams } from "react-router-dom";
import { PageHeader } from "../components/AppShell.js";
import { EventFacts } from "../components/EventSummary.js";
import { Avatar } from "../components/Avatar.js";
import { Button, Notice, Segmented, inputStyle } from "../components/ui.js";
import { hasRole, useAuth } from "../lib/auth.js";
import {
  fetchInvitedIds,
  invitablePeople,
  inviteToTournament,
  uninviteFromTournament,
} from "../lib/invites.js";
import { fetchMembers } from "../lib/members.js";
import {
  clearTournamentLogo,
  fetchOutstandingMatchCount,
  fetchTournaments,
  finishTournament,
  reopenTournament,
  setTournamentContactRequired,
  uploadTournamentLogo,
} from "../lib/tournaments.js";
import { nextUp } from "../lib/organise.js";
import { areEntriesClosed, type Tournament } from "../lib/types.js";
import { LinkRow, PoolRows, useSquadSetup } from "./OrganiseRows.js";
import { RulesRow } from "./TournamentRules.js";
import { AuctionRow } from "./TournamentAuction.js";
import { NewEventForm, NewTournamentForm } from "./Tournaments.js";

/**
 * The organiser's side of tournaments, on pages of their own.
 *
 * These forms and settings used to sit inline on the screens players read — a New tournament
 * form above the events list, an Add event form under each tournament, a settings box on the
 * tournament page — so the reading screens carried organiser furniture most people never use.
 * Now those screens carry one quiet way in each (+ New tournament, and the organise icon beside
 * Share), and everything an organiser does to a tournament is here.
 *
 * Organisers only. The database refuses everybody else regardless (`tournaments_write`,
 * `events_write`); sending them back is signposting, not the lock.
 */
export function NewTournament() {
  const { member } = useAuth();
  const navigate = useNavigate();

  if (!hasRole(member, "organiser")) return <Navigate to="/events" replace />;

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <Link to="/events" className="micro mb-4 inline-block underline">
        ← All events
      </Link>
      <PageHeader eyebrow="Organise" title="New tournament" />
      {/* Straight on to its own Organise page, because the next thing anybody does with a new
          tournament is give it events. */}
      <NewTournamentForm
        onCreated={(id) => navigate(`/tournaments/${id}/manage`, { replace: true })}
        onCancel={() => navigate("/events")}
      />
    </main>
  );
}

export function TournamentManage() {
  const { tournamentId } = useParams<{ tournamentId: string }>();
  const { member } = useAuth();
  const {
    data: tournaments,
    isPending,
    error,
  } = useQuery({
    queryKey: ["tournaments"],
    queryFn: fetchTournaments,
  });
  const tournament = tournaments?.find((candidate) => candidate.id === tournamentId);

  if (!hasRole(member, "organiser")) {
    return <Navigate to={`/tournaments/${tournamentId}`} replace />;
  }

  if (isPending) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <p className="micro">Loading…</p>
      </main>
    );
  }

  if (error || !tournament) {
    return (
      <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
        <Link to="/events" className="micro mb-4 inline-block underline">
          ← All events
        </Link>
        <Notice tone="attention">
          {error
            ? "Couldn't load this tournament. Try again."
            : "That tournament isn't here any more."}
        </Notice>
      </main>
    );
  }

  return <ManageDetail tournament={tournament} isAdmin={hasRole(member, "admin")} />;
}

function ManageDetail({ tournament, isAdmin }: { tournament: Tournament; isAdmin: boolean }) {
  const [adding, setAdding] = useState(false);
  const queryClient = useQueryClient();

  return (
    <main className="mx-auto max-w-2xl px-4 pb-16 pt-6">
      <Link to={`/tournaments/${tournament.id}`} className="micro mb-4 inline-block underline">
        ← {tournament.name}
      </Link>
      <PageHeader eyebrow="Organise" title={tournament.name} />

      {/* In the order an organiser works: what to do next, the events, the auction's steps,
          the settings that are set once, and finishing, last. */}
      {tournament.auction ? <NextUpBlock tournament={tournament} /> : null}

      <section className="mb-8 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
        <h2 className="micro mb-1">Events</h2>
        {/* Each event's own settings — details, description, the draw — live on its Organise
            page; this is the way to each of them, and the place to add another. */}
        <ul>
          {tournament.events.map((event) => (
            <li
              key={event.id}
              className="border-b"
              style={{ borderColor: "var(--color-line-muted)" }}
            >
              <Link
                to={`/events/${event.id}/manage`}
                className="flex min-h-[56px] items-center gap-3 py-2"
              >
                <span className="flex min-w-0 flex-1 flex-col gap-1">
                  <span className="text-base font-bold">{event.name}</span>
                  <EventFacts event={event} showFormat={!tournament.auction} />
                  <span className="font-mono text-[11px] text-ink-muted">
                    Registration {areEntriesClosed(event) ? "closed" : "open"}
                  </span>
                </span>
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.2"
                  aria-hidden
                  className="shrink-0"
                >
                  <path d="M9 5l7 7-7 7" />
                </svg>
              </Link>
            </li>
          ))}
        </ul>
        {tournament.events.length === 0 ? (
          <p className="mt-2 text-sm text-ink-muted">No events yet.</p>
        ) : null}

        {adding ? (
          <div className="mt-4">
            <NewEventForm
              tournamentId={tournament.id}
              auction={tournament.auction}
              onDone={() => {
                setAdding(false);
                // A new pool gives every team a squad in it, so the set-up below is stale.
                void queryClient.invalidateQueries({ queryKey: ["franchises", tournament.id] });
              }}
            />
          </div>
        ) : (
          <button
            type="button"
            onClick={() => setAdding(true)}
            className="mt-3 flex min-h-[52px] w-full items-center justify-center gap-1.5 border-[1.5px] border-dashed text-sm font-semibold text-ink-muted"
            style={{ borderColor: "var(--color-ink-muted)" }}
          >
            <span aria-hidden>+</span> Add an event
          </button>
        )}
      </section>

      {/* Only an auction tournament has teams, seeding, lots and an auction. */}
      {tournament.auction ? <AuctionSteps tournament={tournament} /> : null}

      <section className="mb-8 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
        <h2 className="micro mb-1">Settings</h2>

        {/* Only a private tournament has a list; the database refuses an invite to any other. */}
        {tournament.private ? (
          <div className="mt-4">
            <h3 className="text-[15px] font-semibold">Invited</h3>
            <InviteList tournament={tournament} />
          </div>
        ) : null}

        <div className="mt-6">
          <h3 className="text-[15px] font-semibold">Logo</h3>
          <LogoControl tournament={tournament} />
        </div>

        <div className="mt-6">
          <h3 className="text-[15px] font-semibold">Entry requirements</h3>
          <ContactSwitch tournament={tournament} />
        </div>
      </section>

      <section
        id="finish"
        className="mb-8 border-t-2 pt-4"
        style={{ borderColor: "var(--color-line)" }}
      >
        <h2 className="micro mb-1">Finish</h2>
        <FinishControl tournament={tournament} isAdmin={isAdmin} />
      </section>
    </main>
  );
}

/**
 * The first thing outstanding in the auction's set-up, as one link at the top of the page. A
 * pointer, never a gate: every step below stays open whatever it says.
 */
function NextUpBlock({ tournament }: { tournament: Tournament }) {
  const { data: setup } = useSquadSetup(tournament);
  const next = nextUp(tournament, setup);
  if (!next) return null;

  const inner = (
    <>
      <span className="min-w-0 flex-1">{next.label}</span>
      <svg
        width="18"
        height="18"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.4"
        aria-hidden
        className="shrink-0"
      >
        <path d="M5 12h14M13 6l6 6-6 6" />
      </svg>
    </>
  );
  const linkClass = "flex min-h-[52px] items-center gap-3 px-3.5 py-2 font-bold";

  return (
    <section
      aria-label="Next up"
      className="mb-8 border-2"
      style={{ borderColor: "var(--color-line)" }}
    >
      <div
        className="font-mono flex h-[30px] items-center px-3 text-[11px] font-semibold uppercase tracking-[0.12em]"
        style={{ background: "var(--color-line)", color: "var(--color-ground)" }}
      >
        Next up
      </div>
      {/* Finishing is on this page, so it is a jump down rather than a route. */}
      {next.to.startsWith("#") ? (
        <a href={next.to} className={linkClass}>
          {inner}
        </a>
      ) : (
        <Link to={next.to} className={linkClass}>
          {inner}
        </Link>
      )}
    </section>
  );
}

/**
 * The auction's set-up as numbered steps, in the order it is done. Teams, Seeding and Lots list
 * each pool with where it stands; each row opens that pool's page for the step. No step waits on
 * another: the numbers are an order, not a lock.
 */
function AuctionSteps({ tournament }: { tournament: Tournament }) {
  const { data: setup } = useSquadSetup(tournament);
  const teams = !setup
    ? ""
    : setup.franchises.length === 0
      ? "No teams yet"
      : `${setup.franchises.length} ${setup.franchises.length === 1 ? "team" : "teams"}`;

  return (
    <section className="mb-8 border-t-2 pt-4" style={{ borderColor: "var(--color-line)" }}>
      <h2 className="micro mb-1">Auction</h2>

      <Step number={1} title="Teams">
        <ul>
          <LinkRow
            to={`/tournaments/${tournament.id}/teams`}
            title="Names, badges and taglines"
            facts={teams}
          />
        </ul>
        <h4 className="micro mt-4">Captains and purses</h4>
        <PoolRows tournament={tournament} step="squads" />
      </Step>

      <Step number={2} title="Seeding">
        <PoolRows tournament={tournament} step="seeding" />
      </Step>

      <Step number={3} title="Lots">
        <PoolRows tournament={tournament} step="lots" />
      </Step>

      <Step number={4} title="Rules">
        <RulesRow tournament={tournament} />
      </Step>

      <Step number={5} title="Run">
        <AuctionRow tournament={tournament} />
      </Step>
    </section>
  );
}

function Step({ number, title, children }: { number: number; title: string; children: ReactNode }) {
  return (
    <div className="mt-5">
      <h3 className="flex items-center gap-2.5">
        <span
          aria-hidden
          className="font-mono flex h-6 w-6 shrink-0 items-center justify-center text-[12px] font-semibold"
          style={{ background: "var(--color-line)", color: "var(--color-ground)" }}
        >
          {number}
        </span>
        <span className="font-display text-[22px] uppercase" style={{ fontWeight: 800 }}>
          {title}
        </span>
      </h3>
      <div className="mt-1">{children}</div>
    </div>
  );
}

/**
 * Close the tournament: scores are final, each event's MVP is named, and it leaves the Events
 * list. It asks twice, because it takes the tournament off the list people use and stops results
 * going in. The count of matches in the way is shown before the button is pressed; the database
 * counts again and is the authority, so a match that lands in between still refuses it.
 *
 * Once finished there is no Finish. An admin can reopen, which clears the MVPs with it.
 */
function FinishControl({ tournament, isAdmin }: { tournament: Tournament; isAdmin: boolean }) {
  const queryClient = useQueryClient();
  const [confirming, setConfirming] = useState(false);
  const finished = tournament.finishedAt !== null;

  const { data: outstanding = 0 } = useQuery({
    queryKey: ["outstanding-matches", tournament.id],
    queryFn: () => fetchOutstandingMatchCount(tournament.id),
    enabled: !finished,
  });

  const settle = () => {
    setConfirming(false);
    void queryClient.invalidateQueries({ queryKey: ["tournaments"] });
    void queryClient.invalidateQueries({ queryKey: ["event-mvps", tournament.id] });
    void queryClient.invalidateQueries({ queryKey: ["outstanding-matches", tournament.id] });
    void queryClient.invalidateQueries({ queryKey: ["achievements"] });
  };

  const finish = useMutation({
    mutationFn: () => finishTournament(tournament.id),
    onSuccess: settle,
    onError: () => setConfirming(false),
  });
  const reopen = useMutation({
    mutationFn: () => reopenTournament(tournament.id),
    onSuccess: settle,
  });

  if (finished) {
    return (
      <div className="mt-4">
        <p className="text-[15px] font-semibold">This tournament is finished.</p>
        <p className="mt-1 text-sm text-ink-muted">
          It is off the Events list, takes no more results, and its MVPs are named.
        </p>
        {isAdmin ? (
          <div className="mt-3">
            <Button tone="line" onClick={() => reopen.mutate()} disabled={reopen.isPending}>
              Reopen tournament
            </Button>
            <p className="mt-2 text-sm text-ink-muted">
              Reopening lists it again and clears its MVPs. Finishing again names them afresh.
            </p>
          </div>
        ) : null}
        {reopen.error ? (
          <div className="mt-3">
            <Notice tone="attention">{reopen.error.message}</Notice>
          </div>
        ) : null}
      </div>
    );
  }

  return (
    <div className="mt-4">
      {outstanding > 0 ? (
        <Notice tone="attention">
          {outstanding} {outstanding === 1 ? "match is" : "matches are"} not yet confirmed. Settle{" "}
          {outstanding === 1 ? "it" : "them"} in{" "}
          <Link to="/matches" className="underline">
            Matches
          </Link>{" "}
          first.
        </Notice>
      ) : (
        <p className="text-sm text-ink-muted">
          Names the MVP of each event, takes the tournament off the Events list and stops results
          going in.
        </p>
      )}

      <div className="mt-3">
        {confirming ? (
          <div>
            <p className="mb-2 text-sm font-semibold">
              Finish {tournament.name}? Its scores become final.
            </p>
            <div className="flex gap-2">
              <Button tone="primary" onClick={() => finish.mutate()} disabled={finish.isPending}>
                Yes, finish it
              </Button>
              <Button tone="line" onClick={() => setConfirming(false)}>
                Not yet
              </Button>
            </div>
          </div>
        ) : (
          <Button tone="line" onClick={() => setConfirming(true)} disabled={outstanding > 0}>
            Finish tournament
          </Button>
        )}
      </div>

      {finish.error ? (
        <div className="mt-3">
          <Notice tone="attention">{finish.error.message}</Notice>
        </div>
      ) : null}
    </div>
  );
}

/**
 * "Contact details required", after the fact. Changeable at any time: the database checks at
 * registration and never re-checks an entry, so switching it either way costs nobody a place.
 */
function ContactSwitch({ tournament }: { tournament: Tournament }) {
  const queryClient = useQueryClient();
  const change = useMutation({
    mutationFn: (next: boolean) => setTournamentContactRequired(tournament.id, next),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["tournaments"] }),
  });

  return (
    <div className="mt-4">
      <div className="mb-2 text-[15px] font-semibold">Flat and phone number to enter</div>
      <Segmented
        label="Flat and phone number to enter"
        value={tournament.contactRequired ? "required" : "optional"}
        onChange={(value) => change.mutate(value === "required")}
        options={[
          { value: "optional", label: "Not required" },
          { value: "required", label: "Required" },
        ]}
      />
      <p className="mt-2 text-sm text-ink-muted">
        Changing it never takes anybody&rsquo;s place away: entries are only checked when made.
      </p>
      {change.error ? <Notice tone="attention">{change.error.message}</Notice> : null}
    </div>
  );
}

/**
 * Set, replace or remove the tournament's logo. Organisers only; the Storage policy and
 * `tournaments_write` both refuse anybody else, so hiding this is a courtesy, not the lock.
 */
function LogoControl({ tournament }: { tournament: Tournament }) {
  const queryClient = useQueryClient();
  const input = useRef<HTMLInputElement>(null);
  const done = () => queryClient.invalidateQueries({ queryKey: ["tournaments"] });

  const upload = useMutation({
    mutationFn: (file: File) => uploadTournamentLogo(tournament.id, file),
    onSuccess: done,
  });
  const remove = useMutation({
    mutationFn: () => clearTournamentLogo(tournament.id),
    onSuccess: done,
  });
  const busy = upload.isPending || remove.isPending;
  const error = upload.error ?? remove.error;

  return (
    <div className="mt-3">
      <div className="flex flex-wrap items-center gap-3">
        {/* What is there now, so Replace and Remove say what they would change. */}
        {tournament.logoUrl ? (
          <img
            src={tournament.logoUrl}
            alt={`${tournament.name} logo`}
            className="h-14 w-14 shrink-0 object-contain"
          />
        ) : null}
        <Button type="button" disabled={busy} onClick={() => input.current?.click()}>
          {tournament.logoUrl ? "Replace logo" : "Add logo"}
        </Button>
        {tournament.logoUrl ? (
          <Button type="button" disabled={busy} onClick={() => remove.mutate()}>
            Remove logo
          </Button>
        ) : null}
      </div>
      <input
        ref={input}
        type="file"
        accept="image/*"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared so choosing the same file twice still fires a change event.
          event.target.value = "";
          if (file) upload.mutate(file);
        }}
      />
      {error ? <Notice tone="attention">{error.message}</Notice> : null}
    </div>
  );
}

/**
 * Who a private tournament is for. Organisers only: the list is written by two RPCs that refuse
 * anybody else, so hiding this from players is a courtesy, not the lock.
 *
 * Removing somebody hides the tournament from them again and keeps what they did in it, which
 * the line under the list says, because it is not what "remove" suggests.
 */
function InviteList({ tournament }: { tournament: Tournament }) {
  const queryClient = useQueryClient();
  const [search, setSearch] = useState("");
  const invitedKey = ["tournament-invites", tournament.id];

  const invited = useQuery({ queryKey: invitedKey, queryFn: () => fetchInvitedIds(tournament.id) });
  const members = useQuery({ queryKey: ["members"], queryFn: fetchMembers });

  const done = () => queryClient.invalidateQueries({ queryKey: invitedKey });
  const add = useMutation({
    mutationFn: (memberId: string) => inviteToTournament(tournament.id, memberId),
    onSuccess: () => {
      setSearch("");
      return done();
    },
  });
  const remove = useMutation({
    mutationFn: (memberId: string) => uninviteFromTournament(tournament.id, memberId),
    onSuccess: done,
  });

  const byId = new Map((members.data ?? []).map((member) => [member.id, member]));
  const invitedIds = invited.data ?? [];
  const invitedMembers = invitedIds
    .map((id) => byId.get(id))
    .filter((member) => member !== undefined)
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
  const matches = invitablePeople(members.data ?? [], invitedIds, search).slice(0, 8);
  const busy = add.isPending || remove.isPending;
  const error = invited.error ?? members.error ?? add.error ?? remove.error;

  return (
    <div className="mt-4">
      <ul data-testid="invited-list">
        {invitedMembers.map((member) => (
          <li
            key={member.id}
            className="flex min-h-[52px] items-center gap-3 border-b"
            style={{ borderColor: "var(--color-line-muted)" }}
          >
            <Avatar name={member.displayName} url={member.avatarUrl} size={28} />
            <span className="min-w-0 flex-1 truncate text-base font-bold">
              {member.displayName}
              {member.managed ? <span className="micro ml-2">Child</span> : null}
            </span>
            <Button
              type="button"
              disabled={busy}
              aria-label={`Remove ${member.displayName}`}
              onClick={() => remove.mutate(member.id)}
            >
              Remove
            </Button>
          </li>
        ))}
      </ul>
      {invited.isSuccess && invitedMembers.length === 0 ? (
        <p className="mt-2 text-sm text-ink-muted">Nobody is invited yet.</p>
      ) : null}

      <label className="mt-4 block text-[15px] font-semibold" htmlFor="invite-search">
        Invite someone
      </label>
      <input
        id="invite-search"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        placeholder="Search by name"
        autoComplete="off"
        style={inputStyle}
      />
      <ul data-testid="invite-matches">
        {matches.map((member) => (
          <li key={member.id}>
            <button
              type="button"
              disabled={busy}
              onClick={() => add.mutate(member.id)}
              className="flex min-h-[48px] w-full items-center gap-3 border-b text-left"
              style={{ borderColor: "var(--color-line-muted)" }}
            >
              <Avatar name={member.displayName} url={member.avatarUrl} size={28} />
              <span className="flex-1 truncate font-semibold">
                {member.displayName}
                {member.managed ? <span className="micro ml-2">Child</span> : null}
              </span>
              <span className="micro">Invite</span>
            </button>
          </li>
        ))}
      </ul>
      {search.trim() && matches.length === 0 ? (
        <p className="mt-2 text-sm text-ink-muted">No approved member by that name.</p>
      ) : null}

      <p className="mt-3 text-sm text-ink-muted">
        A parent can see this tournament when their child is invited. Removing someone hides it from
        them again; their entries and results stay.
      </p>
      {error ? <Notice tone="attention">{error.message}</Notice> : null}
    </div>
  );
}
